<script setup lang="ts">
import { truthy } from '#shared/filter'
import defu from 'defu'
import { twMerge } from 'tailwind-merge'

interface Layout {
  regular: string
  images: string
  wide: string
  wideOffset: string
  full: string
}

const props = defineProps<{
  layout?: Partial<Layout>
  headerImage?: CrystallizeArticle['components']['headerImage']
}>()

const article = inject(diKeys.article)

const blocks = computed(() => article?.value?.components?.body?.filter(truthy) ?? [])

type Block = typeof blocks.value[number]

interface Group {
  key: number
  blocks: Block[]
  class?: string
}

const DEFAULT_GROUP_CLASS = 'bg-white py-l lg:py-2xl rounded-4xl w-full'

function getClasses(block: Block): string {
  switch (block.type) {
    case 'faq':
      return 'bg-transparent'
    case 'generic-cta':
      return twMerge(DEFAULT_GROUP_CLASS, 'overflow-hidden')
    default:
      return DEFAULT_GROUP_CLASS
  }
}

function emptyGroup(key: number): Group {
  return {
    key,
    blocks: [],
  }
}

const groups = computed(() => {
  const result: NonEmptyArray<Group> = [emptyGroup(0)]

  for (const block of blocks.value) {
    if (block.type === 'separator') {
      result.push(emptyGroup(result.length))
    }
    else {
      const classes = getClasses(block)
      const lastGroup = result[result.length - 1]!
      lastGroup.blocks.push(block)
      if (classes) {
        lastGroup.class = twMerge(lastGroup.class, classes)
      }
    }
  }

  return result.filter(it => it.blocks.length > 0)
})

const layout = computed<Layout>(() => defu(props.layout, {
  regular: 'col-span-4 lg:col-start-3 2xl:col-start-4 2xl:col-span-6',
  images: 'lg:col-span-5 lg:col-start-3 2xl:col-span-7 2xl:col-start-4',
  wide: 'col-span-4 lg:col-start-2 lg:col-span-6 2xl:col-start-3 2xl:col-span-8',
  wideOffset: 'col-span-4 lg:col-start-2 lg:col-span-5 2xl:col-span-7 2xl:col-start-3',
  full: 'col-span-full',
}))
</script>

<template>
  <section data-container class="flex flex-col gap-y-l">
    <CrystallizeImage
      :image="headerImage"
      hide-caption
      class="bg-white rounded-t-4xl p-m -mb-l"
      img-class="rounded-2xl"
    />
    <ContentGrid
      v-for="(group, i) in groups" :key="`group-${group.key}`"
      class="gap-y-l lg:gap-y-16"
      :class="[group.class, {
        'rounded-t-none pt-l': headerImage && i === 0,
      }]"
    >
      <template v-for="(block, j) in group.blocks" :key="`${block.type}-${j}`">
        <NettbutikkArticleFactBox v-if="block.type === 'fact-box'" :class="layout.regular" :data="block.value" />
        <NettbutikkArticleLawQuote v-else-if="block.type === 'law-quote'" :class="layout.wideOffset" :data="block.value" />
        <NettbutikkArticleQuote v-else-if="block.type === 'quote'" class="-my-xl" :class="layout.regular" :data="block.value" />
        <NettbutikkArticleParagraph
          v-else-if="block.type === 'paragraphs'"
          :data="block.value"
          :class="[
            layout.regular,
            { [layout.images]: block.value.images },
          ]"
        />
        <NettbutikkArticleGenericCta
          v-else-if="block.type === 'generic-cta'"
          :class="article?.components?.template?.value === 'standard' ? layout.wide : layout.full"
          :data="block.value"
        />
        <NettbutikkArticleProductCta v-else-if="block.type === 'product-cta'" :class="layout.wide" :data="block.value" />
        <CrystallizeImage
          v-else-if="block.type === 'images'"
          :class="layout.wide"
          class="text-carbon-400 text-base flex flex-col gap-s"
          img-class="rounded-2xl"
          :image="block.value?.firstImage"
        >
          <template #meta="{ meta }">
            <span v-if="meta.copyright" class="text-xs text-carbon-300">
              © {{ meta.copyright }}
            </span>
          </template>
        </CrystallizeImage>
        <HubspotForm v-if="block.type === 'hubspot-form'" :id="block.value.guid" :class="layout.regular" class="flex flex-col gap-s">
          <template #header>
            <h2 v-if="block.value.title" class="title-m col-span-full" v-text="block.value.title" />

            <template v-if="block.value.text">
              <CrystallizeRichText v-for="(textBlock, textIndex) in block.value.text" :key="textIndex" :block="textBlock" />
            </template>
          </template>
        </HubspotForm>
        <NettbutikkArticleFaq
          v-if="block.type === 'faq'"
          :title="block.value.title"
          :description="block.value.description"
          :questions="block.value.questions"
          :aside="block.value.aside"
          :class="block.value.aside ? layout.full : layout.regular"
          class="-mx-[--content-padding]"
        />
        <NettbutikkArticleCookieTable v-if="block.type === 'cookie-table'" :data="block.value" :class="layout.regular" />
        <NettbutikkArticleAuthor v-if="block.type === 'person'" :data="block.value" class="col-span-full lg:px-l" />
      </template>

      <NettbutikkArticleFootnotes
        v-if="i === groups.length - 1"
        :footnotes="article?.components?.footnotesSimple"
        :class="layout.regular"
      />
    </ContentGrid>
  </section>
</template>

<style scoped>
[data-container] {
  view-transition-name: article-body;
  contain: layout;
}
</style>

<style>
::view-transition-image-pair(article-body) {
  overflow: clip;
}
</style>
